import { DiscountApplicationType } from "@intelligems/ig-types/dtos/offers";
import {
  EXPERIENCE_MODULE_BY_OFFER_TYPE,
  ExperienceModule,
  ExperienceModuleList,
} from "@intelligems/ig-types/dtos/experience-modules";

export const PRODUCT_VERSION_2026 = "2026_module_v1";

export const UNLIMITED_ID = "unlimited";
export const SMART_CONTENT_ID = "smart-content";
export const SMART_CHECKOUT_ID = "smart-checkout";
export const SMART_MERCHANDISING_ID = "smart-merchandising";
export const SMART_PRICING_LIMITED_ID = "smart-pricing-limited";
export const SMART_PRICING_UNLIMITED_ID = "smart-pricing-unlimited";
export const SMART_SHIPPING_ID = "smart-shipping";

export const SMART_PRICING_VARIANTS: ReadonlySet<string> = new Set([
  SMART_PRICING_LIMITED_ID,
  SMART_PRICING_UNLIMITED_ID,
]);

// The billing module that gates each experience module. `SMART_PRICING_LIMITED_ID`
// covers both Smart Pricing tiers: the gate model treats any Smart Pricing
// variant as satisfying the pricing gate.
export const MODULE_ID_BY_EXPERIENCE_MODULE: Readonly<
  Record<ExperienceModule, string>
> = {
  content: SMART_CONTENT_ID,
  checkout: SMART_CHECKOUT_ID,
  postPurchase: SMART_CHECKOUT_ID,
  pricing: SMART_PRICING_LIMITED_ID,
  merchandising: SMART_MERCHANDISING_ID,
  shipping: SMART_SHIPPING_ID,
};

export const experienceModulesForModuleId = (
  moduleId: string
): ExperienceModule[] => {
  const gateModuleId = SMART_PRICING_VARIANTS.has(moduleId)
    ? SMART_PRICING_LIMITED_ID
    : moduleId;
  return ExperienceModuleList.filter(
    (module) => MODULE_ID_BY_EXPERIENCE_MODULE[module] === gateModuleId
  );
};

// Offer type → experience module → billing module. An offer with no type yet
// has nothing to gate on, so it resolves to no module rather than defaulting
// to Smart Merchandising.
export const moduleIdForOfferType = (
  type: DiscountApplicationType
): string | undefined =>
  type === ""
    ? undefined
    : MODULE_ID_BY_EXPERIENCE_MODULE[EXPERIENCE_MODULE_BY_OFFER_TYPE[type]];

export type SmartPricingAnalyticsTier = "T1" | "T2" | "n/a";

// Maps a billing entity id to its Smart Pricing analytics tier: the Limited
// variant is T1, the Unlimited variant is T2, everything else is not a Smart
// Pricing purchase ("n/a").
export const smartPricingAnalyticsTier = (
  id: string
): SmartPricingAnalyticsTier => {
  if (id === SMART_PRICING_LIMITED_ID) {
    return "T1";
  }
  if (id === SMART_PRICING_UNLIMITED_ID) {
    return "T2";
  }
  return "n/a";
};

// Orders/month at or above this level puts an account in the HIGH volume tier,
// where adding Smart Pricing routes through the kickoff-confirmation step
// instead of the self-serve module-selection dialog.
export const HIGH_ORDER_VOLUME_THRESHOLD = 5000;

// Orders/month at or above this level puts an account in the MID volume tier.
// Module-local until a second reader needs it: callers want the bucket, and a
// second inline comparison against this boundary is the duplication
// `orderVolumeTier` exists to remove.
const MID_ORDER_VOLUME_THRESHOLD = 2000;

export type OrderVolumeTier = "low" | "mid" | "high";

// Where an org sits in the trial→paid lifecycle. One home, because the billing
// module gate and every account-shaped analytics payload segment on this axis,
// and must stay value-identical for a report to break both down together.
export type AccountUserState = "trial" | "paid_other" | "expired";

// The one order-volume bucketing. It lives here, outside any feature module,
// because the same buckets drive the billing module gate, the Smart Pricing
// kickoff routing, and account-shaped analytics payloads on pages that never
// load the billing workflow — so the thresholds must exist exactly once.
//
// Ordered high-first so the fallthrough is the conservative bucket. Callers
// gate real entitlements on this — the Unlimited card only advertises a
// dedicated CSM in the high tier — and nothing re-validates the org-summary
// response on the client, so an absent or non-numeric volume must land on
// "low" and promise nothing. Testing the low bound first would invert that:
// `undefined < MID_ORDER_VOLUME_THRESHOLD` is false.
export const orderVolumeTier = (ordersPerMonth: number): OrderVolumeTier => {
  if (ordersPerMonth >= HIGH_ORDER_VOLUME_THRESHOLD) {
    return "high";
  }
  if (ordersPerMonth >= MID_ORDER_VOLUME_THRESHOLD) {
    return "mid";
  }
  return "low";
};

const SALES_NUDGE_ORDER_VOLUME_THRESHOLD = 500;

export const qualifiesForSalesNudge = (ordersPerMonth: number): boolean =>
  ordersPerMonth >= SALES_NUDGE_ORDER_VOLUME_THRESHOLD;
